// Minimal, dependency-free dropdown controller for click-to-open menus
// Uses .is-open on items with children and updates ARIA attributes.

(function() {
  const root = document;
  const bySel = (sel, ctx = root) => ctx.querySelector(sel);
  const allSel = (sel, ctx = root) => Array.from(ctx.querySelectorAll(sel));

  const closeAll = () => {
    // Blur any focused menu toggle to prevent focus-based reopening
    const focused = document.activeElement;
    if (focused?.matches('[data-dropdown-toggle], [data-panel-toggle]')) {
      focused.blur();
    }
    
    for (const open of root.querySelectorAll('.nav__item--has-children.is-open')) {
      open.classList.remove('is-open');
      const toggle = open.querySelector('[data-dropdown-toggle]');
      const submenu = open.querySelector('.submenu, .mega-menu');
      toggle?.setAttribute('aria-expanded', 'false');
      submenu?.setAttribute('aria-hidden', 'true');
    }
    for (const open of root.querySelectorAll('.submenu__item--has-children.is-open')) {
      open.classList.remove('is-open');
      const toggle = open.querySelector('[data-panel-toggle]');
      const panel = open.querySelector('.submenu__panel');
      toggle?.setAttribute('aria-expanded', 'false');
      panel?.setAttribute('aria-hidden', 'true');
    }
  };

  // Ensure dropdowns/panels stay within viewport horizontally & vertically
  function adjustWithinViewport(container) {
    try {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const headerRect = bySel('.site-header')?.getBoundingClientRect();

      // Adjust level-2 dropdowns
      for (const submenu of allSel('.nav__item--has-children > .submenu', container)) {
        submenu.classList.remove('submenu--align-right', 'submenu--scrollable');
        const rect = submenu.getBoundingClientRect();
        if (rect.right > vw - 8) {
          submenu.classList.add('submenu--align-right');
        }
        const availableTop = rect.top - (headerRect ? Math.max(0, headerRect.top) : 0);
        const availableBottom = vh - rect.top;
        const maxHeight = Math.max(220, Math.min(availableTop + availableBottom - 20, vh - 40));
        if (rect.height > maxHeight) {
          submenu.style.maxHeight = maxHeight + 'px';
          submenu.classList.add('submenu--scrollable');
        } else {
          submenu.style.maxHeight = '';
        }
      }

      // Adjust level-3 panels
      for (const panel of allSel('.submenu__item--has-children > .submenu__panel', container)) {
        panel.classList.remove('submenu__panel--flip', 'submenu__panel--scrollable');
        const rect = panel.getBoundingClientRect();
        if (rect.right > vw - 8) {
          panel.classList.add('submenu__panel--flip');
        }
        const availableTop = rect.top - (headerRect ? Math.max(0, headerRect.top) : 0);
        const availableBottom = vh - rect.top;
        const maxHeight = Math.max(220, Math.min(availableTop + availableBottom - 20, vh - 40));
        if (rect.height > maxHeight) {
          panel.style.maxHeight = maxHeight + 'px';
          panel.classList.add('submenu__panel--scrollable');
        } else {
          panel.style.maxHeight = '';
        }
      }
    } catch (_) { /* ignore */ }
  }

  // Mobile hamburger toggle
  const toggleButton = bySel('[data-menu-toggle]');
  const primaryNav = bySel('.site-header__nav');
  if (toggleButton && primaryNav) {
    toggleButton.addEventListener('click', () => {
      const isOpen = primaryNav.classList.toggle('is-open');
      toggleButton.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      const openLabel = toggleButton.getAttribute('data-open-label') || 'Open menu';
      const closeLabel = toggleButton.getAttribute('data-close-label') || 'Close menu';
      toggleButton.setAttribute('aria-label', isOpen ? closeLabel : openLabel);

      if (!isOpen) closeAll();
      // On mobile, when opening the menu, ensure the active branch is expanded
      if (isOpen && window.matchMedia('(max-width: 959px)').matches) {
        ensureActiveBranchOpen();
      }
    });
  }

  root.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-dropdown-toggle]');
    const panelToggle = e.target.closest('[data-panel-toggle]');
    const insideMegaMenu = e.target.closest('.mega-menu');
    const insideSubmenu = e.target.closest('.submenu');

    // Click outside: close all
    if (!toggle && !panelToggle && !insideMegaMenu && !insideSubmenu && !e.target.closest('.site-header__nav')) {
      closeAll();
      return;
    }

    // If clicking inside mega menu content (not on toggle), don't close
    if (!toggle && !panelToggle && (insideMegaMenu || insideSubmenu)) {
      return;
    }

    if (toggle) {
      // Respect modifier-clicks (new tab/window)
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      
      const item = toggle.closest('.nav__item--has-children');
      const isCurrentlyOpen = item?.classList.contains('is-open');
      
      // If already open, close everything (same as clicking outside)
      if (isCurrentlyOpen) {
        closeAll();
        return;
      }
      
      // Close all first, then open the clicked one
      closeAll();
      
      const submenu = item?.querySelector(':scope > .submenu, :scope > .mega-menu');
      item?.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
      submenu?.setAttribute('aria-hidden', 'false');

      if (item) adjustWithinViewport(item);

      // When opening a parent, ensure all nested panels start closed (avoid any auto-open)
      if (open) {
        for (const nested of item.querySelectorAll('.submenu__item--has-children.is-open')) {
          nested.classList.remove('is-open');
          const nToggle = nested.querySelector('[data-panel-toggle]');
          const nPanel = nested.querySelector('.submenu__panel');
          nToggle?.setAttribute('aria-expanded', 'false');
          nPanel?.setAttribute('aria-hidden', 'true');
        }
      }
    }

    if (panelToggle) {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      e.preventDefault();
      const subItem = panelToggle.closest('.submenu__item--has-children');
      const panel = subItem?.querySelector('.submenu__panel');
      const open = subItem?.classList.toggle('is-open');
      panelToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      panel?.setAttribute('aria-hidden', open ? 'false' : 'true');

  if (open) adjustWithinViewport(subItem);

      // Close sibling panels
      for (const sibling of subItem.parentElement.querySelectorAll('.submenu__item--has-children')) {
        if (sibling !== subItem) {
          sibling.classList.remove('is-open');
          const sibToggle = sibling.querySelector('[data-panel-toggle]');
          const sibPanel = sibling.querySelector('.submenu__panel');
          sibToggle?.setAttribute('aria-expanded', 'false');
          sibPanel?.setAttribute('aria-hidden', 'true');
        }
      }
    }
  });

  // Escape key closes menus
  root.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeAll();
  });

  // Adjust on resize and orientation changes
  let adjustT;
  ['resize', 'orientationchange'].forEach((ev) => {
    window.addEventListener(ev, () => {
      clearTimeout(adjustT);
      adjustT = setTimeout(() => adjustWithinViewport(root), 100);
    }, { passive: true });
  });

  // Expand active branch helper (mobile)
  function ensureActiveBranchOpen() {
    try {
      const currentPath = location.pathname.replace(/\/+$/, '') || '/';
      const links = allSel('.site-header__nav a[href]');
      let active = null;
      for (const a of links) {
        const href = a.getAttribute('href');
        if (!href) continue;
        const tmp = document.createElement('a');
        tmp.href = href;
        const path = (tmp.pathname || '').replace(/\/+$/, '') || '/';
        if (path === currentPath) { active = a; break; }
      }
      if (!active) return;

      const topItem = active.closest('.nav__item--has-children');
      if (topItem) {
        topItem.classList.add('is-open');
        const toggle = topItem.querySelector('[data-dropdown-toggle]');
        const submenu = topItem.querySelector('.submenu');
        toggle?.setAttribute('aria-expanded', 'true');
        submenu?.setAttribute('aria-hidden', 'false');
      }

      const subItem = active.closest('.submenu__item--has-children');
      if (subItem) {
        subItem.classList.add('is-open');
        const pToggle = subItem.querySelector('[data-panel-toggle]');
        const panel = subItem.querySelector('.submenu__panel');
        pToggle?.setAttribute('aria-expanded', 'true');
        panel?.setAttribute('aria-hidden', 'false');
      }
    } catch (_) { /* ignore */ }
  }

  // --- AJAX Add to Cart for spec table ---
  const showToast = (msg) => {
    const el = bySel('#Toast');
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
    el.classList.add('toast--show');
    setTimeout(() => el.classList.remove('toast--show'), 2500);
  };

  const handleAjaxAdd = async (form) => {
    const addedLabel = form.getAttribute('data-added-label') || 'Added to cart';
    const errorLabel = form.getAttribute('data-error-label') || 'Could not add to cart';
    try {
      const data = new FormData(form);
      const res = await fetch(form.action + '.json', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: data
      });
      if (!res.ok) throw new Error('request');
      const json = await res.json();
      // Shopify /cart/add.json returns the added line item object, not { item: ... }
      const looksLikeLineItem = json && (json.id || json.key || json.title);
      const looksLikeItemsArray = json && Array.isArray(json.items);
      if (!looksLikeLineItem && !looksLikeItemsArray) throw new Error('bad');
      showToast(addedLabel);
    } catch (e) {
      showToast(errorLabel);
    }
  };

  root.addEventListener('submit', (e) => {
    const form = e.target.closest('form[data-ajax-add-to-cart]');
    if (!form) return;
    // Avoid navigation, do async add
    e.preventDefault();
    handleAjaxAdd(form);
  });
})();
